import _Rollbar from 'rollbar';
import { LogLevel } from '../../core/levels';
import { LogEntry } from '../../core/logEntry';
import { PluginFunction, PluginOptions } from './plugin';

const REQUEST_OBJECT_KEYS: Readonly<string[]> = [
  'headers',
  'protocol',
  'url',
  'method',
  'body',
  'route',
  'user_id',
  'user',
];

/**
 * RollbarConfiguration is the transport and instance configuration for
 * using the rollbar transport.
 */
type RollbarConfiguration = Pick<_Rollbar.Configuration, 'accessToken'> & {
  level?: LogLevel;
};

/**
 * Basic transport for outputting to Rollbar
 */
class RollbarTransport {
  rollbar: _Rollbar;

  constructor({ accessToken, level = LogLevel.ERROR }: RollbarConfiguration) {
    const conf = {
      accessToken,
      reportLevel: level as _Rollbar.Level,
    };
    this.rollbar = new _Rollbar(conf);
  }

  /**
   * extractRequestFromMeta pulls request-specific data from a meta object for rollbar.
   *
   * See: https://docs.rollbar.com/docs/rollbarjs-configuration-reference#rollbarlog
   */
  private extractRequestFromMeta(meta: Record<string, string>): [Record<string, unknown>, Record<string, unknown>] {
    const request: Record<string, unknown> = {};
    const rest: Record<string, unknown> = {};

    Object.entries(meta).forEach(([k, v]) => {
      if (REQUEST_OBJECT_KEYS.includes(k)) {
        request[k] = v;
      } else {
        rest[k] = v;
      }
    });

    return [request, rest];
  }

  /**
   * toRollbar converts a LogEntry into a rollbar logging signature
   */
  private toRollbar(entry: LogEntry) {
    // FIXME: We should be using triple-beam to reference the uniq symbols to
    // name the level and message field. However, using these symbols throws:
    // Type 'unique symbol' cannot be used as an index type.
    //
    // These symbols are defined here:
    // https://github.com/winstonjs/triple-beam/blob/master/index.js
    const { message, cause, ...srcMeta } = entry;
    const [requestMeta, meta] = this.extractRequestFromMeta(srcMeta);

    const resolvedMessage = typeof cause === 'object' ? cause : message;

    return { message: resolvedMessage, requestMeta, meta };
  }

  private onEmit(rollbarErr: unknown) {
    if (rollbarErr) {
      console.error(
        JSON.stringify({
          message: 'Unable to log to rollbar',
          error: rollbarErr,
        })
      );
    }
  }

  warn(entry: LogEntry) {
    const { message, requestMeta, meta } = this.toRollbar(entry);
    this.rollbar.warn(message, requestMeta, meta, this.onEmit);
  }

  error(entry: LogEntry) {
    const { message, requestMeta, meta } = this.toRollbar(entry);
    this.rollbar.error(message, requestMeta, meta, this.onEmit);
  }
}

/**
 * Rollbar is the actual plugin function used by loglevel to emit errors
 * to rollbar.
 */
const Rollbar: (transport: RollbarTransport) => PluginFunction =
  (transport) =>
  ({ method, message, methodName }: PluginOptions) => {
    if (typeof message === 'object') {
      switch (methodName) {
        case 'warn':
          transport.warn({ ...message });
          break;
        case 'error':
          transport.error({ ...message });
          break;
      }
    }

    return method(message);
  };

export type { RollbarConfiguration };
export { RollbarTransport, Rollbar };
